import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Toolbar } from 'primereact/toolbar'
import React, { useState } from 'react'
import { emptyUser } from '../types/consts'
import ConfirmDeleteUser from './confirm-delete-user'
import ConfirmDeleteManyUsers from './confirm-delete-users'

import './users-view.scss'
import { User } from 'domain/user'
import { useAuth } from 'layout/context/auth-context'
import { useLoading } from 'layout/context/loading-context'
import { useToast } from 'layout/context/toast-context'
import { useUser } from '../hooks/useUser'
import { UpdateUserDto, CreateUserDto } from '../types/user.types'
import UserStepper from './user-stepper'

const UsersView = () => {
  const { showSuccess, showError } = useToast()
  const [user, setUser] = useState<User>(emptyUser)
  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const [userDialog, setUserDialog] = useState(false)
  const [deleteUserDialog, setDeleteUserDialog] = useState(false)
  const [deleteUsersDialog, setDeleteUsersDialog] = useState(false)
  const [submitted, setSubmitted] = useState<boolean>(false)

  const { users, fetchUsers, createUser, updateUser, deleteUser } = useUser()

  const { clientId } = useAuth()
  const { isLoading } = useLoading()

  React.useEffect(() => {
    if (clientId) {
      fetchUsers({ clientId })
    }
  }, [clientId, fetchUsers])

  const openNew = () => {
    if (clientId) {
      setUser({ ...emptyUser, clientId })
      setSubmitted(false)
      setUserDialog(true)
    }
  }

  const hideDialog = () => {
    setSubmitted(false)
    setUserDialog(false)
  }

  const hideDeleteUserDialog = () => {
    setDeleteUserDialog(false)
  }

  const hideDeleteUsersDialog = () => {
    setDeleteUsersDialog(false)
  }

  const saveUser = async () => {
    if (!clientId) {
      return
    }
    setSubmitted(true)

    if (user.name.trim() && user.login.trim() && user.email.trim()) {
      let result

      if (user.id) {
        const updateData: UpdateUserDto = {
          id: user.id,
          name: user.name,
          login: user.login,
          email: user.email,
          phoneNumber: user.phoneNumber,
          active: user.active,
          blocked: user.blocked,
          mainContactPerson: user.mainContactPerson,
          clientId: user.clientId,
        }

        if (user.password) {
          updateData.password = user.password
        }

        result = await updateUser(user.id, updateData)

        if (result) {
          showSuccess('Sucesso', 'Usuário atualizado com sucesso')
        } else {
          showError('Erro', 'Falha ao atualizar usuário')
        }
      } else {
        const createData: CreateUserDto = {
          clientId: user.clientId,
          name: user.name,
          login: user.login,
          password: user.password,
          email: user.email,
          phoneNumber: user.phoneNumber,
          active: user.active,
          blocked: user.blocked,
          mainContactPerson: user.mainContactPerson,
        }

        result = await createUser(createData, clientId)

        if (result) {
          showSuccess('Sucesso', 'Usuário criado com sucesso')
        } else {
          showError('Erro', 'Falha ao criar usuário')
        }
      }

      setUserDialog(false)
      setUser(emptyUser)
      fetchUsers({ clientId })
    }
  }

  const editUser = (user: User) => {
    setUser({ ...user, password: '' })
    setUserDialog(true)
  }

  const confirmDeleteUser = (user: User) => {
    setUser(user)
    setDeleteUserDialog(true)
  }

  const confirmDeleteSelected = () => {
    setDeleteUsersDialog(true)
  }

  const deleteUserItem = async () => {
    const success = await deleteUser(user.id)

    if (success) {
      showSuccess('Sucesso', 'Usuário excluído com sucesso')
      setDeleteUserDialog(false)
      setUser(emptyUser)
    } else {
      showError('Erro', 'Falha ao excluir usuário')
    }
  }

  const deleteSelectedUsers = async () => {
    let successCount = 0

    for (const user of selectedUsers) {
      const success = await deleteUser(user.id)
      if (success) successCount++
    }

    if (successCount > 0) {
      showSuccess('Sucesso', `${successCount} usuários excluídos`)
    }

    setDeleteUsersDialog(false)
    setSelectedUsers([])
  }

  const leftToolbarTemplate = () => {
    return (
      <div className='users-toolbar-left'>
        <Button
          label='Novo Usuário'
          icon='pi pi-plus'
          className='users-btn-new'
          onClick={openNew}
          disabled={!clientId}
        />
        <Button
          label='Excluir Selecionados'
          icon='pi pi-trash'
          severity='danger'
          className='users-btn-delete'
          onClick={confirmDeleteSelected}
          disabled={!selectedUsers || !selectedUsers.length}
        />
      </div>
    )
  }

  const rightToolbarTemplate = () => {
    return (
      <div className='users-toolbar-right'>
        {clientId && (
          <div className='users-stats'>
            <span className='users-badge'>{users.length}</span>
            <span className='users-stats-text'>Total de Usuários</span>
          </div>
        )}
      </div>
    )
  }

  const actionBodyTemplate = (rowData: User) => {
    return (
      <div className='users-actions'>
        <Button
          icon='pi pi-pencil'
          rounded
          className='users-action-edit'
          onClick={() => editUser(rowData)}
          tooltip='Editar'
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon='pi pi-trash'
          rounded
          severity='danger'
          className='users-action-delete'
          onClick={() => confirmDeleteUser(rowData)}
          tooltip='Excluir'
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    )
  }

  const activeBodyTemplate = (rowData: User) => {
    return (
      <span
        className={`users-status-badge ${rowData.active ? 'users-status-active' : 'users-status-inactive'
          }`}
      >
        {rowData.active ? 'Ativo' : 'Inativo'}
      </span>
    )
  }

  const blockedBodyTemplate = (rowData: User) => {
    return (
      <span
        className={`users-status-badge ${rowData.blocked ? 'users-status-blocked' : 'users-status-unblocked'
          }`}
      >
        {rowData.blocked ? 'Bloqueado' : 'Liberado'}
      </span>
    )
  }

  const mainContactBodyTemplate = (rowData: User) => {
    return (
      <span
        className={`users-contact-badge ${rowData.mainContactPerson
          ? 'users-contact-main'
          : 'users-contact-regular'
          }`}
      >
        {rowData.mainContactPerson ? 'Principal' : 'Regular'}
      </span>
    )
  }

  const nameBodyTemplate = (rowData: User) => {
    return (
      <div className='users-name-cell'>
        <i className='pi pi-user users-icon' />
        <span className='users-name-text'>{rowData.name}</span>
      </div>
    )
  }

  const emailBodyTemplate = (rowData: User) => {
    return (
      <div className='users-email-cell'>
        <i className='pi pi-envelope users-icon-small' />
        <span>{rowData.email}</span>
      </div>
    )
  }

  const phoneBodyTemplate = (rowData: User) => {
    return (
      <div className='users-phone-cell'>
        <i className='pi pi-phone users-icon-small' />
        <span>{rowData.phoneNumber || 'Não informado'}</span>
      </div>
    )
  }

  const lastLoginBodyTemplate = (rowData: User) => {
    return (
      <div className='users-login-cell'>
        <i className='pi pi-clock users-icon-small' />
        <span>{new Date(rowData.lastLogin).toLocaleString('pt-BR')}</span>
      </div>
    )
  }

  return (
    <div className='users-container'>
      <div className='users-header'>
        <div className='users-title-section'>
          <i className='pi pi-users users-main-icon' />
          <h1 className='users-title'>Gerenciamento de Usuários</h1>
        </div>
        <div className='users-subtitle'>
          Gerencie contas de usuários e permissões de acesso
        </div>
      </div>

      <Card className='users-card'>
        <Toolbar
          className='users-toolbar'
          left={leftToolbarTemplate}
          right={rightToolbarTemplate}
        />

        <DataTable
          value={users}
          selection={selectedUsers}
          onSelectionChange={(e) => setSelectedUsers(e.value)}
          selectionMode='multiple'
          dataKey='id'
          paginator
          loading={isLoading}
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
          className='users-datatable'
          paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
          currentPageReportTemplate='Mostrando {first} a {last} de {totalRecords} usuários'
          emptyMessage='Nenhum usuário encontrado para este cliente.'
          responsiveLayout='scroll'
          stripedRows
          showGridlines
        >
          <Column
            selectionMode='multiple'
            headerStyle={{ width: '3rem' }}
            className='users-selection-column'
          />
          <Column
            field='name'
            header='Nome'
            sortable
            body={nameBodyTemplate}
            headerStyle={{ minWidth: '15rem' }}
            className='users-name-column'
          />
          <Column
            field='login'
            header='Login'
            sortable
            headerStyle={{ minWidth: '12rem' }}
            className='users-login-column'
          />
          <Column
            field='lastLogin'
            header='Último Acesso'
            body={lastLoginBodyTemplate}
            sortable
            headerStyle={{ minWidth: '12rem' }}
            className='users-lastlogin-column'
          />
          <Column
            field='active'
            header='Status'
            body={activeBodyTemplate}
            sortable
            headerStyle={{ minWidth: '8rem' }}
            className='users-status-column'
          />
          <Column
            field='blocked'
            header='Bloqueio'
            body={blockedBodyTemplate}
            sortable
            headerStyle={{ minWidth: '8rem' }}
            className='users-blocked-column'
          />
          <Column
            field='mainContactPerson'
            header='Contato'
            body={mainContactBodyTemplate}
            sortable
            headerStyle={{ minWidth: '10rem' }}
            className='users-contact-column'
          />
          <Column
            body={actionBodyTemplate}
            header='Ações'
            headerStyle={{ minWidth: '10rem' }}
            className='users-actions-column'
          />
        </DataTable>

        <UserStepper
          hideDialog={hideDialog}
          saveUser={saveUser}
          isLoading={isLoading}
          setUser={setUser}
          showDialog={userDialog}
          submitted={submitted}
          user={user}
        />
      </Card>

      <ConfirmDeleteManyUsers
        deleteSelectedUsers={deleteSelectedUsers}
        hideDialog={hideDeleteUsersDialog}
        usersCount={selectedUsers.length}
        visible={deleteUsersDialog}
      />

      <ConfirmDeleteUser
        deleteUserItem={deleteUserItem}
        hideDialog={hideDeleteUserDialog}
        user={user}
        visible={deleteUserDialog}
      />
    </div>
  )
}

export default UsersView
